import { type FeatureItem, useFeaturesStore } from '@/features/Features';
import type { FeatureGroup } from '@/features/Features/types';
import { isKaskoTyp, KASKOTYP } from '@/features/Sparten/shared/types';
import type { KfzFeatureItem } from '@/features/Sparten/shared/types/kfz/types';
import { useInputStore } from '@/features/Sparten';
import cloneDeep from 'lodash/cloneDeep';
import reduce from 'lodash/reduce';
import dot from 'dot-object';
import get from 'lodash/get';

export function filterCategories(): FeatureGroup<KfzFeatureItem>[] {
  const inputStore = useInputStore();
  const featuresStore = useFeaturesStore();

  const features = featuresStore.data as FeatureGroup<KfzFeatureItem>[];

  const { kaskoTyp } = inputStore;

  if (kaskoTyp === KASKOTYP.VOLLKASKO || kaskoTyp === KASKOTYP.TEILKASKO) {
    return features;
  }

  return features.filter((feature) => {
    if (feature.meta === null) return true;

    const { meta } = feature;

    if (!isKaskoTyp(meta)) {
      return false;
    }

    return kaskoTyp === KASKOTYP.HAFTPFLICHT && meta === KASKOTYP.HAFTPFLICHT;
  });
}

export function filterItemsByMetaData(
  features: FeatureGroup<KfzFeatureItem | undefined>[],
): FeatureGroup<KfzFeatureItem | undefined>[] {
  const inputStore = useInputStore();

  const copyFeatures = cloneDeep(features);

  /**
   * Überprüft, ob die Meta-Daten eines Items mit den Werten im Store übereinstimmen.
   *
   * @param meta - Die Meta-Daten des Items, die überprüft werden.
   * @param store - Der Store, gegen den die Meta-Daten geprüft werden.
   * @returns true, wenn alle Meta-Daten mit den entsprechenden Werten im Store übereinstimmen, andernfalls false.
   */
  const doesMetaMatchStore = (
    meta: Record<string, any>,
    store: Record<string, any>,
  ): boolean => {
    // Simplify object by transforming it into the dotted variant, to easily access store values
    dot.keepArray = true;
    const dottedMeta = dot.dot(meta);
    dot.keepArray = false;
    // Verwende Object.entries und forEach, um durch die Meta-Daten zu iterieren
    return Object.entries(dottedMeta).every(([path, metaValue]) => {
      const storeValue = get(store, path);

      if (Array.isArray(metaValue)) {
        // Falls der Meta-Wert ein Array ist, prüfen ob der Store-Wert enthalten ist
        return metaValue.includes(storeValue);
      }

      // Direkter Vergleich für primitive Werte (z.B. Strings, Zahlen)
      return metaValue === storeValue;
    });
  };

  // Filtert die Feature-Gruppen basierend auf den Meta-Daten und den Werten im Store
  return copyFeatures.map((featureGroup) => {
    const filteredItems = reduce(
      featureGroup.items,
      (acc: FeatureItem<KfzFeatureItem | undefined>[], item) => {
        const { meta } = item;

        // Füge das Item hinzu, wenn keine Meta-Daten vorhanden sind oder diese übereinstimmen
        if (!meta || doesMetaMatchStore(meta, inputStore)) {
          acc.push(item);
        }

        return acc;
      },
      [],
    );

    // Rückgabe der gefilterten Feature-Gruppe mit den gefilterten Items
    return { ...featureGroup, items: filteredItems };
  });
}
